<template>
  <div class="app-check-sim-card">
    <slot name="header">
      <h2 class="text-3xl"><span class="uppercase">sim</span> Informationen</h2>
    </slot>
    <div class="container-box">
      <div class="content-limiter">
        <p>
          Hier kannst du den Status deiner <span class="uppercase">sim</span>-Karte prüfen und deine <span class="uppercase">puk</span> einsehen. Den <span class="uppercase">puk</span> (Super <span class="uppercase">pin</span>) benötigst du zum
          Beispiel wenn deine <span class="uppercase">sim</span> durch mehrmalige Falscheingaben deines <span class="uppercase">pins</span> gesperrt wurde. Bitte stelle sicher, dass niemand
          die angezeigten Daten mitlesen kann.
        </p>
        <div class="data">
          <div class="col">
            <p class="label">
              <slot name="lblSimType"> <span class="uppercase">sim</span>-Type:</slot>
            </p>
            <p class="value">{{ simTypeLabel }}</p>
          </div>
          <div class="msisdn col">
            <p class="label">
              <slot name="lblMsisdn"> Rufnummer</slot>
            </p>
            <p class="value">{{ msisdn }}</p>
          </div>
          <div class="status col">
            <p class="label">
              <slot v-if="isTypeSimCard" name="lblStatus"> <span class="uppercase">sim</span>-Kartenstatus:</slot>
              <slot v-if="isTypeESim" name="lblESimStatus"> e<span class="uppercase">sim</span>-Status:</slot>
            </p>
            <p class="value">{{ status }}</p>
          </div>
          <div class="icc-id col">
            <p class="label">
              <slot v-if="isTypeSimCard" name="lblIccId"><span class="uppercase">sim</span>-Kartenseriennummer:</slot>
              <slot v-if="isTypeESim" name="lblESimSerialNumber">e<span class="uppercase">sim</span>-Seriennummer:</slot>
            </p>
            <p class="value">{{ iccId }}</p>
          </div>
          <div class="puk col">
            <p class="label">
              <slot name="lblPUK"><span class="uppercase">puk</span> (Super <span class="uppercase">pin</span>):</slot>
            </p>
            <p class="value">
              {{ pukView }}
              <button @click="togglePuk()" class="text-base">
                <dyn-slot :slot-data="slotData">
                  <slot name="btnView"><span class="uppercase">puk</span></slot>
                </dyn-slot>
                {{ slotData.maskShown ? 'anzeigen' : 'ausblenden' }}
              </button>
            </p>
          </div>
          <div v-if="isTypeESim && canInstallESim" class="setup-button">
            <a :href="setupUrl" class="button button--primary" type="button">
              <dyn-slot :slot-data="slotData">
                <slot name="btnInstall">e<span class="uppercase">sim</span> Profil einrichten</slot>
              </dyn-slot>
            </a>
          </div>
        </div>
        <div>
          <div class="info-box">
            <div class="flex-align-center">
              <span class="icon" aria-hidden="true"></span>
              <span class="text-base-semibold">
                Wie überprüfe ich, ob das e<span class="uppercase">sim</span> Profil auf meinem <span lang="en">Smartphone</span> eingerichtet ist?</span
              >
            </div>

            <p class="text-sm">
              <strong>1.<span lang="en">Single-</span>-<span class="uppercase">sim</span>-Modell:</strong> Überprüfe, ob dein <span lang="en">Smartphone</span> Empfang hat. Wenn ja, dann wurde Einrichtung
              erfolgreich durchgeführt.
            </p>

            <p class="text-sm">
              <strong>2.<span lang="en">Dual-</span>-<span class="uppercase">sim</span>-Modelle:</strong>Überprüfe, ob beide <span class="uppercase">sim</span>-Karten in der Netzanzeige angezeigt werden. Wenn ja,
              dann wurde Einrichtung erfolgreich durchgeführt.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { wrapQuery } from '@/helper/wrapGQLQuery'
import gql from 'graphql-tag'
import preFilledInput from '@/mixins/preFilledInput'
import DynSlot from '@/components/generic/DynSlot.vue'
import transaction from '@/mixins/transaction'
import { SimTypes } from '@/helper/SimTypes'

export default {
  name: 'ViewCheckSimCard',
  components: { DynSlot },
  mixins: [transaction('checkSIMCard'), preFilledInput('pukView', 'mask')],
  data: function() {
    return {
      currentCustomer: null,
      SimTypes
    }
  },
  props: {
    mask: {
      type: String,
      default: '*****'
    },
    setupUrl: {
      type: String,
      default: '/mein-lidl-connect/sim-karte-verwalten/esim-profil-einrichten.html'
    }
  },
  computed: {
    msisdn: function() {
      return this.currentCustomer?.contract?.msisdn
    },
    status: function() {
      let result = 'inaktiv'

      if (this.isTypeSimCard && this.currentCustomer?.contract?.simCard?.status === 'ACTIVE') {
        result = 'aktiv'
      } else if (this.isTypeESim && this.currentCustomer?.contract?.simCard?.status === 'ACTIVE') {
        result = 'aktiv'
      }

      return result
    },
    iccId: function() {
      let result = 'inaktiv'
      if (this.currentCustomer?.contract?.simCard?.iccId) {
        result = this.currentCustomer?.contract?.simCard?.iccId
      }
      return result
    },
    simType: function() {
      return this.currentCustomer?.contract?.simCard?.type
    },
    maskShown: function() {
      return this.pukView === this.mask
    },
    slotData: function() {
      return {
        maskShown: this.maskShown
      }
    },
    isTypeSimCard: function() {
      return this.simType === SimTypes.simCardCode
    },
    isTypeESim: function() {
      return this.simType === SimTypes.eSimCode
    },
    simTypeLabel: function() {
      return this.isTypeSimCard ? SimTypes.simCardLabel : SimTypes.eSimLabel
    },
    eSimPuk: function() {
      return this.currentCustomer?.contract?.simCard?.puk
    },
    canInstallESim: function() {
      return this.currentCustomer?.contract?.simCard?.status === 'ACTIVE'
    }
  },
  apollo: {
    allIdentMethods: function() {
      return wrapQuery(
        gql`
          query checkSIMCard {
            currentCustomer {
              contract {
                msisdn
                simCard {
                  iccId
                  status
                  puk
                  type
                }
              }
            }
          }
        `,
        this,
        'currentCustomer',
        {},
        () => {}
      )
    }
  },
  methods: {
    togglePuk: function() {
      if (this.pukView === this.mask) {
        this.pukView = this.currentCustomer?.contract?.simCard?.puk
      } else this.pukView = this.mask
    }
  }
}
</script>
